import styles from './index.module.css'

import React from 'react'

import { Box, Card, Flex, Heading } from 'components/ds'

import type { ActiveAddOn } from './add-ons-row'
import AddOnsRow from './add-ons-row'
import BillingDetailsRow from './billing-details-row'
import type { BillingPeriod } from './billing-period-row'
import BillingPeriodRow from './billing-period-row'
import type { BillingSummaryDiscount } from './billing-summary-row'
import BillingSummaryRow from './billing-summary-row'
import ManageSubscriptionButton from './manage-subscription-button'
import type { Price } from './price'
import type { CardDetails } from './utils'

export type { ActiveAddOn, BillingPeriod, CardDetails, Price }

type Props = {
  /** Null for periods the app doesn't support yet, which are shown without saying how often they bill */
  period: BillingPeriod | null
  /** Next renewal date, already formatted */
  renewsOn: string | null
  /** Scheduled cancellation date, already formatted */
  expiresOn: string | null
  /** Amount due at the next renewal, already formatted in the subscription currency */
  amount: string | null
  /** Amount before applied discounts, already formatted in the subscription currency */
  amountBeforeDiscounts?: string | null
  discounts?: BillingSummaryDiscount[]
  card: CardDetails | null
  billedTo: string[]
  activeAddOns: ActiveAddOn[]
  /** In the plan's billing period */
  addOnsTotal?: Price | null
  /** Plan, seats and tracks, rendered at the top of the card */
  planSection?: React.ReactNode
  /** Cards for the add-ons the business can still buy, rendered below the card */
  addOnOffers?: React.ReactNode
  /** Chargebee pricing page, opened in a new tab; until it arrives the CTA shows as loading */
  manageSubscriptionUrl?: string
  /** The pricing page couldn't be loaded; the CTA says so in a tooltip */
  manageSubscriptionDisabled?: boolean
  /** Called when Manage subscription opens the pricing page */
  onManageSubscriptionOpen?: () => void
  /** False for read-only plans (Supervisor, Enterprise…), which get Contact sales instead of Manage subscription */
  canChangePlan?: boolean
  /** Sales mailto, the CTA for read-only plans */
  contactSalesHref?: string
  /** Non-admins see the CTAs unavailable, with a tooltip saying why; Contact sales stays open to everyone */
  isAdmin: boolean
  hasChargebeeScriptLoaded: boolean
  hasChargebeeScriptFailed: boolean
  chargebeeScriptLoader?: React.ReactNode
  cancelSubscription?: React.ReactNode
  /** Shown in place of the card when the subscription couldn't be loaded */
  loadError?: React.ReactNode
}

/**
 * "Your subscription" section of the Billing page: add-ons, billing period, billing details and
 * billing summary pulled from Chargebee, with the actions to manage or cancel the subscription.
 */
const YourSubscription = ({
  period,
  renewsOn,
  expiresOn,
  amount,
  amountBeforeDiscounts,
  discounts,
  card,
  billedTo,
  activeAddOns,
  addOnsTotal,
  planSection,
  addOnOffers,
  manageSubscriptionUrl,
  manageSubscriptionDisabled,
  onManageSubscriptionOpen,
  canChangePlan = true,
  contactSalesHref,
  isAdmin,
  hasChargebeeScriptLoaded,
  hasChargebeeScriptFailed,
  chargebeeScriptLoader,
  cancelSubscription,
  loadError,
}: Props): React.ReactNode => {
  const hasRows = Boolean(
    planSection ||
      activeAddOns.length > 0 ||
      renewsOn ||
      expiresOn ||
      card ||
      billedTo.length > 0,
  )

  return (
    <Flex direction="column" gap="5">
      <Flex direction="column" gap="4">
        <Heading as="h2" size="3" weight="medium">
          Your subscription
        </Heading>
        <Box>
          {loadError ? (
            <Box mb="3">{loadError}</Box>
          ) : (
            hasRows && (
              <Card size="2" mb="3">
                <Flex direction="column" className={styles.rows}>
                  {planSection}
                  {activeAddOns.length > 0 && (
                    <AddOnsRow addOns={activeAddOns} total={addOnsTotal} />
                  )}
                  {renewsOn || expiresOn ? (
                    <BillingPeriodRow
                      period={period}
                      renewsOn={renewsOn}
                      expiresOn={expiresOn}
                    />
                  ) : null}
                  {card || billedTo.length > 0 ? (
                    <BillingDetailsRow
                      card={card}
                      billedTo={billedTo}
                      isAdmin={isAdmin}
                      hasChargebeeScriptLoaded={hasChargebeeScriptLoaded}
                      hasChargebeeScriptFailed={hasChargebeeScriptFailed}
                    />
                  ) : null}
                  {renewsOn && amount && !expiresOn ? (
                    <BillingSummaryRow
                      period={period}
                      renewsOn={renewsOn}
                      amount={amount}
                      amountBeforeDiscounts={amountBeforeDiscounts}
                      discounts={discounts}
                    />
                  ) : null}
                </Flex>
              </Card>
            )
          )}
          <Flex align="center" gap="2">
            <ManageSubscriptionButton
              url={canChangePlan ? manageSubscriptionUrl : contactSalesHref}
              isAdmin={isAdmin}
              disabled={manageSubscriptionDisabled}
              contactSales={!canChangePlan}
              onOpen={canChangePlan ? onManageSubscriptionOpen : undefined}
            />
            {/* Ghost buttons pull outward by their padding, which would swallow the gap */}
            {cancelSubscription && <Box ml="3">{cancelSubscription}</Box>}
            {chargebeeScriptLoader}
          </Flex>
        </Box>
      </Flex>
      {addOnOffers && (
        <Flex direction="column" gap="4">
          <Heading as="h2" size="3" weight="medium">
            Add ons
          </Heading>
          {addOnOffers}
        </Flex>
      )}
    </Flex>
  )
}

export default YourSubscription
